import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Icon } from "@iconify/react";
import { Link, useSearchParams } from "react-router-dom";
import { Col } from "react-bootstrap";
import { getHotelAdmins, edithoteladmin } from "../store/slices/auth";
import ShowSkeleton from "../utils/helpers/skeleton";
import StatusModal from "../utils/Modals/statusModal";
import ReactPaginate from "react-paginate";

const HotelAdmin = () => {
  const [data, setData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionType, setActionType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isloading, setisLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // 🟢 search input state
  const [debouncedSearch, setDebouncedSearch] = useState(""); // 🟢 debounce state

  const itemsPerPage = 25;
  const dispatch = useDispatch();
  const [search, setSearch] = useSearchParams();
  const querypage = parseInt(search.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(querypage);
  const [totalData, setTotalData] = useState(0);

  // 🟢 Debounce search term (delay 500ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // 🟢 Fetch data when page or search changes
  useEffect(() => {
    setisLoading(true);
    getHAdminLists();
  }, [currentPage, debouncedSearch]);

  const item = {
    limit: itemsPerPage,
    page: currentPage,
    hotelName: debouncedSearch || undefined, // pass search to backend
  };

  const handlePageChange = (event) => {
    const newPage = event.selected + 1;
    setCurrentPage(newPage);
    search.set("page", newPage);
    setSearch(search, { replace: true });
  };

  const getHAdminLists = () => {
    dispatch(getHotelAdmins(item))
      .unwrap()
      .then((data) => {
        setData(data?.adminData || []);
        setTotalData(data?.totalDataCount || 0);
      })
      .catch(({ message }) => console.error(message))
      .finally(() => {
        setisLoading(false);
      });
  };

  const totalPages = Math.ceil(totalData / itemsPerPage);

  const openStatusModal = (_id, status) => {
    setSelectedItem({ id: _id, status });
    setActionType(status === 1 ? "deactivate" : "activate");
    setShowModal(true);
  };

  const handleStatusChange = () => {
    if (selectedItem) {
      const { id, status } = selectedItem;
      const updatedStatus = status === 1 ? 0 : 1;

      const payload = { id, status: updatedStatus };

      dispatch(edithoteladmin(payload))
        .unwrap()
        .then(() => {
          getHAdminLists();
          setShowModal(false);
        })
        .catch((error) => {
          console.error("Status update error: ", error);
        });
    } else {
      console.error("No selected item set.");
    }
  };

  return (
    <>
      <StatusModal
        showModal={showModal}
        actionType={actionType}
        setShowModal={setShowModal}
        handleDelete={handleStatusChange}
      />

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <h6 className="fw-semibold mb-0">Hotel Owners</h6>
        <ul className="d-flex align-items-center gap-2">
          <li className="fw-medium">
            <Link
              to="/admin/home"
              className="d-flex align-items-center gap-1 hover-text-primary"
            >
              Home
            </Link>
          </li>
          <li> &gt;</li>
          <li className="fw-medium">Hotel Owners</li>
        </ul>
      </div>

      <div className="card h-100 p-0 radius-12">
        <div className="card-header bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          {/* 🟢 Search input field */}
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search by hotel name..."
              style={{ width: "250px" }}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // reset to first page on new search
              }}
            />
          </div>

          <Link
            to="/admin/add-hotel-admin"
            className="btn btn-primary text-md btn-sm px-8 py-8 radius-8 d-flex align-items-center gap-2"
          >
            Add Hotel Owner
          </Link>
        </div>

        <div className="card-body px-24 py-2">
          <div className="table-responsive scroll-sm">
            {isloading ? (
              <Col lg={12} xs={12} md={12} align="center">
                {ShowSkeleton()}
              </Col>
            ) : (
              <table className="table striped-table mb-0">
                <thead>
                  <tr>
                    <th>S.L</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Username</th>
                    <th>Mobile</th>
                    <th>Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 ? (
                    data.map((item, index) => (
                      <tr key={index}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>{item.fullName}</td>
                        <td>{item.email}</td>
                        <td>{item.username}</td>
                        <td>{item.mobile}</td>
                        <td>
                          {item.status === 0 ? (
                            <Link
                              onClick={() =>
                                openStatusModal(item._id, item.status)
                              }
                              className="bg-danger-focus text-danger-600 text-md fw-medium d-flex justify-content-center align-items-center"
                              style={{
                                borderRadius: "10px",
                                padding: "2px",
                                width: "80px",
                                fontSize: "14px",
                              }}
                            >
                              Deactivate
                            </Link>
                          ) : (
                            <Link
                              onClick={() =>
                                openStatusModal(item._id, item.status)
                              }
                              className="bg-success-focus text-success-600 fw-medium text-md d-flex justify-content-center align-items-center"
                              style={{
                                borderRadius: "10px",
                                padding: "2px",
                                fontSize: "14px",
                                width: "80px",
                              }}
                            >
                              Activate
                            </Link>
                          )}
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center">
                            <Link
                              to={`/admin/hoteladmin-details/${item._id}`}
                              className="text-md bg-warning-focus text-warning-600 w-28-px h-28-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon icon="lucide:eye" />
                            </Link>
                            <Link
                              to={`/admin/edit-hotel-admin/${item._id}`}
                              className="text-md bg-success-focus text-success-600 w-28-px h-28-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon icon="lucide:edit" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No hotel admins found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className="d-flex justify-content-between mt-3">
            <span>&nbsp;</span>
            <ReactPaginate
              previousLabel={"«"}
              nextLabel={"»"}
              breakLabel={"..."}
              pageCount={totalPages}
              marginPagesDisplayed={1}
              pageRangeDisplayed={3}
              onPageChange={handlePageChange}
              containerClassName={"pagination"}
              activeLinkClassName={"active"}
              pageLinkClassName={"page-link"}
              previousLinkClassName={"page-link"}
              nextLinkClassName={"page-link"}
              breakLinkClassName={"page-link"}
              forcePage={currentPage - 1}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default HotelAdmin;
